import { useRef, type ReactElement, type UIEvent } from 'react';
import { Box, Typography } from '@mui/material';
import type { DiffToken } from '../lib/diffEngine';
import styles from '../text-compare.module.scss';

export type DiffViewMode = 'split' | 'unified';

export type DiffViewerProps = {
  tokens: DiffToken[];
  mode: DiffViewMode;
};

const classFor = (type: DiffToken['type']): string | undefined => {
  if (type === 'added') return styles.added;
  if (type === 'removed') return styles.removed;
  return undefined;
};

const renderTokens = (tokens: DiffToken[]): ReactElement[] =>
  tokens.map((t, i) => (
    <span key={i} className={classFor(t.type)}>
      {t.text}
    </span>
  ));

const DiffViewer = ({ tokens, mode }: DiffViewerProps) => {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  // Защита от обратной связи: прокрутка одной колонки программно двигает
  // вторую, что иначе вызвало бы её onScroll и зациклило события.
  const syncingRef = useRef(false);

  const syncScroll = (source: 'left' | 'right') => (e: UIEvent<HTMLDivElement>) => {
    if (syncingRef.current) {
      syncingRef.current = false;
      return;
    }
    const target = source === 'left' ? rightRef.current : leftRef.current;
    if (!target) return;
    syncingRef.current = true;
    target.scrollTop = e.currentTarget.scrollTop;
    target.scrollLeft = e.currentTarget.scrollLeft;
  };

  const hasChanges = tokens.some((t) => t.type !== 'equal');

  if (!hasChanges) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--shell-text-tertiary)',
        }}
      >
        <Typography variant="body2">Различий нет — тексты идентичны</Typography>
      </Box>
    );
  }

  if (mode === 'unified') {
    return <div className={styles.unified}>{renderTokens(tokens)}</div>;
  }

  // split: слева equal+removed, справа equal+added
  const leftTokens = tokens.filter((t) => t.type !== 'added');
  const rightTokens = tokens.filter((t) => t.type !== 'removed');

  return (
    <div className={styles.columns}>
      <div ref={leftRef} className={styles.column} onScroll={syncScroll('left')}>
        <div className={styles.columnLabel}>Было</div>
        {renderTokens(leftTokens)}
      </div>
      <div ref={rightRef} className={styles.column} onScroll={syncScroll('right')}>
        <div className={styles.columnLabel}>Стало</div>
        {renderTokens(rightTokens)}
      </div>
    </div>
  );
};

export default DiffViewer;
